import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { Feather, User, Mail, Bell, CreditCard, ArrowLeft, LogOut, Check, Calendar, BookOpen, Inbox, Clock, Sparkles } from 'lucide-react';

import { API } from '../config';

export default function AccountPage() {
  const { user, token, logout, isSubscribed, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [loading, setLoading] = useState(false);
  const [letters, setLetters] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subResponse, lettersResponse] = await Promise.all([
        axios.get(`${API}/account/subscription`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }),
        axios.get(`${API}/letters`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        })
      ]);
      
      setSubscriptionInfo(subResponse.data);
      setEmailNotifications(subResponse.data.email_notifications);
      setPushNotifications(subResponse.data.push_notifications);
      setLetters(lettersResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const updateNotificationPrefs = async (email, push) => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/account/notifications`,
        { email_notifications: email, push_notifications: push },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      toast.success('Preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailToggle = (checked) => {
    setEmailNotifications(checked);
    updateNotificationPrefs(checked, pushNotifications);
  };

  const handlePushToggle = async (checked) => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    setLoading(true);
    try {
      if (checked) {
        // Request permission and subscribe
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error('Notification permission denied');
          setLoading(false);
          return;
        }

        // Get VAPID key and subscribe
        const vapidResponse = await axios.get(`${API}/push/vapid-key`);
        const { publicKey } = vapidResponse.data;

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });

        // Save subscription to server
        await axios.post(
          `${API}/push/subscribe`,
          {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
              auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Push notifications enabled');
      } else {
        // Unsubscribe
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
        
        await axios.delete(`${API}/push/unsubscribe`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success('Push notifications disabled');
      }

      setPushNotifications(checked);
      updateNotificationPrefs(emailNotifications, checked);
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      toast.error('Failed to update push notification settings');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert VAPID key
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Calculate reading progress
  const accessibleLetters = letters.filter(l => l.teaser || isSubscribed);
  const totalLetters = 52;
  const receivedCount = accessibleLetters.length;
  const progressPercent = Math.round((receivedCount / totalLetters) * 100);

  // Get latest letter
  const latestLetter = accessibleLetters.length > 0 
    ? accessibleLetters.reduce((a, b) => a.week_number > b.week_number ? a : b)
    : null;

  return (
    <div className="min-h-screen bg-parchment">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-parchment/95 backdrop-blur-sm border-b border-[#E5E0D0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_ink-nostalgia/artifacts/xhguxwkc_34799.png"
              alt="Unspoken Hearts"
              className="h-10"
            />
          </Link>
          
          <Link 
            to="/archive" 
            className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors"
            data-testid="archive-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-lora">Back to Letters</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <header className="mb-12 animate-fade-in">
          <h1 className="font-playfair text-4xl text-charcoal mb-2">Your Account</h1>
          <p className="font-lora text-charcoal-muted">Manage your letters and preferences.</p>
        </header>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Profile Card */}
            <Card className="card-letter animate-fade-in" data-testid="profile-card">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-rose-muted/20 rounded-full flex items-center justify-center flex-shrink-0">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-wine" />
                  )}
                </div>
                <div>
                  <h2 className="font-playfair text-2xl text-charcoal">{user?.name}</h2>
                  <p className="font-lora text-charcoal-muted">{user?.email}</p>
                  <p className="font-lora text-sm text-charcoal-light mt-1">
                    Member since {formatDate(user?.created_at)}
                  </p>
                </div>
              </div>
            </Card>

            {/* Letters Reception Card */}
            <Card className="card-letter animate-fade-in delay-100" data-testid="letters-reception-card">
              <div className="flex items-center gap-3 mb-6">
                <Inbox className="w-6 h-6 text-wine" />
                <h2 className="font-playfair text-xl text-charcoal">Letter Reception</h2>
              </div>

              <div className="space-y-6">
                {/* Progress */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-lora text-charcoal">Your Journey Progress</span>
                    <span className="font-playfair text-wine">{receivedCount} / {totalLetters}</span>
                  </div>
                  <Progress value={progressPercent} className="h-2 bg-parchment-paper" />
                  <p className="font-lora text-sm text-charcoal-muted mt-2">
                    {progressPercent}% of the story unlocked
                  </p>
                </div>

                {/* Latest Letter */}
                {latestLetter && (
                  <div className="p-4 bg-parchment-paper border border-[#E5E0D0] rounded-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-lora text-xs text-charcoal-muted mb-1">Latest Received</p>
                        <h3 className="font-playfair text-lg text-charcoal">{latestLetter.title}</h3>
                        <p className="font-dancing text-sepia">{latestLetter.date}</p>
                      </div>
                      <Button
                        onClick={() => navigate(`/letter/${latestLetter.week_number}`)}
                        variant="ghost"
                        size="sm"
                        className="text-wine hover:text-wine/80"
                        data-testid="read-latest-btn"
                      >
                        <BookOpen className="w-4 h-4 mr-1" />
                        Read
                      </Button>
                    </div>
                  </div>
                )}

                {/* Next Letter Info */}
                {isSubscribed && receivedCount < totalLetters && (
                  <div className="flex items-center gap-3 p-4 bg-rose-muted/10 border border-rose-muted/20 rounded-sm">
                    <Clock className="w-5 h-5 text-wine flex-shrink-0" />
                    <div>
                      <p className="font-lora text-charcoal">Next letter arriving soon</p>
                      <p className="font-lora text-sm text-charcoal-muted">
                        Week {receivedCount + 1} will be delivered to your inbox
                      </p>
                    </div>
                  </div>
                )}

                <Button 
                  onClick={() => navigate('/archive')}
                  className="btn-secondary w-full justify-center"
                  data-testid="view-all-letters-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  View All Letters
                </Button>
              </div>
            </Card>

            {/* Notifications Card */}
            <Card className="card-letter animate-fade-in delay-200" data-testid="notifications-card">
              <div className="flex items-center gap-3 mb-6">
                <Bell className="w-6 h-6 text-wine" />
                <h2 className="font-playfair text-xl text-charcoal">Delivery Preferences</h2>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-charcoal-muted" />
                    <div>
                      <Label htmlFor="email-notifications" className="font-lora text-charcoal cursor-pointer">
                        Email Delivery
                      </Label>
                      <p className="font-lora text-sm text-charcoal-muted">
                        Receive new letters in your inbox
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="email-notifications"
                    checked={emailNotifications}
                    onCheckedChange={handleEmailToggle}
                    disabled={loading}
                    data-testid="email-notifications-switch"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-charcoal-muted" />
                    <div>
                      <Label htmlFor="push-notifications" className="font-lora text-charcoal cursor-pointer">
                        Push Notifications
                      </Label>
                      <p className="font-lora text-sm text-charcoal-muted">
                        Get notified when new letters arrive
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="push-notifications"
                    checked={pushNotifications}
                    onCheckedChange={handlePushToggle}
                    disabled={loading}
                    data-testid="push-notifications-switch"
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Subscription Card */}
            <Card className="card-letter animate-fade-in delay-100" data-testid="subscription-card">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-6 h-6 text-wine" />
                <h2 className="font-playfair text-xl text-charcoal">Subscription</h2>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-parchment-paper rounded-sm">
                  <div className="flex items-center gap-2 mb-2">
                    {isSubscribed ? (
                      <>
                        <Sparkles className="w-5 h-5 text-wine" />
                        <span className="font-playfair text-wine">Active Subscriber</span>
                      </>
                    ) : (
                      <span className="font-playfair text-charcoal-muted">Free Account</span>
                    )}
                  </div>
                  {isSubscribed && user?.subscription_end_date && (
                    <p className="font-lora text-sm text-charcoal-muted">
                      Renews {formatDate(user.subscription_end_date)}
                    </p>
                  )}
                </div>

                {isSubscribed ? (
                  <div className="p-4 bg-rose-muted/10 border border-rose-muted/20 rounded-sm">
                    <p className="font-lora text-sm text-charcoal-muted">
                      Thank you for being a subscriber! You have full access to all letters.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="font-lora text-sm text-charcoal-muted">
                      Unlock all 52 letters and receive weekly deliveries.
                    </p>
                    <Button 
                      onClick={() => navigate('/subscription')}
                      className="btn-primary w-full justify-center"
                      data-testid="subscribe-btn"
                    >
                      Subscribe $7/month
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* Quick Stats */}
            <Card className="p-6 bg-parchment-paper border border-[#E5E0D0] animate-fade-in delay-200">
              <h3 className="font-playfair text-lg text-charcoal mb-4">Your Reading Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-lora text-charcoal-muted">Letters received</span>
                  <span className="font-playfair text-charcoal">{receivedCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-lora text-charcoal-muted">Weeks remaining</span>
                  <span className="font-playfair text-charcoal">{totalLetters - receivedCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-lora text-charcoal-muted">Story progress</span>
                  <span className="font-playfair text-charcoal">{progressPercent}%</span>
                </div>
              </div>
            </Card>

            {/* Logout Button */}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-center font-lora text-charcoal-muted hover:text-wine hover:bg-rose-muted/10 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E0D0] mt-12">
        <div className="max-w-4xl mx-auto px-6 py-8 flex items-center justify-between">
          <p className="font-dancing text-lg text-sepia">"Moments preserved in ink"</p>
          <p className="font-lora text-sm text-charcoal-muted">© 2026 Unspoken Hearts</p>
        </div>
      </footer>
    </div>
  );
}
